/**
 * Screencast filmstrip writer (workspace side).
 *
 * `@chatsdet/trace-tools`'s `extractScreencastFrames` is the pure, decimation-free
 * extractor. This module owns the workspace concerns it can't: aligning frames to
 * the run-start axis (via the shared `TraceClock`), bounding volume (dedup +
 * failure-window-aware decimation), and writing `browser/screencast/` + its index.
 *
 * Unlike the synthetic per-action frames (`reconstructAndWriteArtifacts`), this
 * runs purely from the zip — no headless browser — so it produces images even
 * when reconstruction soft-fails, and at evaluate/wait moments that have no
 * action snapshot at all.
 */

import * as fs from 'node:fs/promises';
import { extractScreencastFrames, type TraceAction } from '@chatsdet/trace-tools';
import {
  resolveWorkspacePath,
  screencastFrameBasename,
  WORKSPACE_PATHS,
} from './paths.js';
import type { TraceClock } from './_shared.js';

/** Keep one frame every ~250ms in the idle stretches between always-keep frames. */
const DEFAULT_CADENCE_MS = 250;
/** Hard ceiling on kept frames (disk + agent read budget). Always-keep frames are exempt. */
const DEFAULT_MAX_FRAMES = 400;
/** Pad the failed step's bracket so the frames just before/after it survive at full density. */
const FAILURE_WINDOW_PAD_MS = 3000;

type KeptReason = 'failure-window' | 'action' | 'step-boundary' | 'cadence';

/** A surviving filmstrip frame, projected for step rollups and the index. */
export interface KeptScreencastFrame {
  frameIndex: number;
  timestampMs: number;
  wallTimeMs: number;
  pageId: string;
  file: string;
  keptReason: KeptReason;
  /** True when raw frames were dropped between this frame and the prior kept one. */
  decimatedBefore: boolean;
}

export interface ScreencastWriteInput {
  workspaceRoot: string;
  tracePath: string;
  /** Parsed trace actions — used to anchor frames near real interactions. */
  actions: ReadonlyArray<TraceAction>;
  /** Shared clock so frames land on the same axis as `actions.jsonl.timestampMs`. */
  clock: TraceClock;
  /** Absolute ms of every step-started/step-completed annotation. */
  stepBoundaryMs: ReadonlyArray<number>;
  /** The failed step's padded work window (absolute ms) — kept at full density. */
  failureWindow: { startMs: number; endMs: number } | null;
  cadenceMs?: number;
  maxFrames?: number;
}

export interface ScreencastWriteResult {
  /** True if anything was missing/failed (workspace marked partial). */
  partial: boolean;
  /** The kept frames, for the step-rollup builder to interleave by timestamp. */
  frames: KeptScreencastFrame[];
}

interface MappedFrame {
  absTs: number;
  wallTimeMs: number;
  pageId: string;
  jpeg: Buffer;
}

/** Index (into `sorted`) of the frame nearest `targetTs`. Assumes `sorted` ascending by `ts`. */
const nearestIndex = (sorted: ReadonlyArray<{ idx: number; ts: number }>, targetTs: number): number | null => {
  if (sorted.length === 0) return null;
  let lo = 0;
  let hi = sorted.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid]!.ts < targetTs) lo = mid + 1;
    else hi = mid;
  }
  // `lo` is the first frame at/after target; compare it with its predecessor.
  const after = sorted[lo]!;
  const before = lo > 0 ? sorted[lo - 1]! : null;
  if (before && Math.abs(before.ts - targetTs) <= Math.abs(after.ts - targetTs)) {
    return before.idx;
  }
  return after.idx;
};

/**
 * Extract, align, decimate, and write the screencast filmstrip. Returns
 * `{ partial, frames }`; `partial=true` (and an index marker) when there are no
 * screencast frames (older traces / `screenshots:false`) or writes fail.
 */
export const writeScreencast = async (
  input: ScreencastWriteInput,
): Promise<ScreencastWriteResult> => {
  const cadenceMs = input.cadenceMs ?? DEFAULT_CADENCE_MS;
  const maxFrames = input.maxFrames ?? DEFAULT_MAX_FRAMES;
  const indexPath = resolveWorkspacePath(input.workspaceRoot, WORKSPACE_PATHS.browserScreencastIndex);

  let rawFrames;
  try {
    rawFrames = extractScreencastFrames(input.tracePath, { dedupeConsecutive: true });
  } catch {
    await writeIndexMarker(indexPath, 'screencast-extract-failed');
    return { partial: true, frames: [] };
  }

  if (rawFrames.length === 0) {
    await writeIndexMarker(indexPath, 'no-screencast-frames');
    return { partial: true, frames: [] };
  }

  // Align every frame to the run-start axis. `extractScreencastFrames` already
  // sorted by wall time, and `absFromWall` is monotonic, so `mapped` stays sorted.
  // `frameSwapWallTime` carries sub-ms precision; round to integer ms so
  // timestamps format cleanly (T+S.mmm) and filenames stay `<NNNN>-<intMs>.jpg`.
  const mapped: MappedFrame[] = rawFrames.map((f) => ({
    absTs: Math.round(input.clock.absFromWall(f.frameSwapWallTimeMs)),
    wallTimeMs: f.frameSwapWallTimeMs,
    pageId: f.pageId,
    jpeg: f.jpeg,
  }));

  // Per-page sorted index for same-page nearest-frame lookups (popups have their
  // own stream — never match a main-page action to a popup frame).
  const globalSorted = mapped.map((m, idx) => ({ idx, ts: m.absTs }));
  const perPageSorted = new Map<string, Array<{ idx: number; ts: number }>>();
  mapped.forEach((m, idx) => {
    const list = perPageSorted.get(m.pageId) ?? [];
    list.push({ idx, ts: m.absTs });
    perPageSorted.set(m.pageId, list);
  });

  // Reason per kept frame, highest priority wins.
  const kept = new Map<number, KeptReason>();
  const setIfHigher = (idx: number | null, reason: KeptReason): void => {
    if (idx === null) return;
    const existing = kept.get(idx);
    if (existing === undefined || PRIORITY[reason] > PRIORITY[existing]) kept.set(idx, reason);
  };

  // 1. Failure window — keep every frame inside it (post-dedup).
  if (input.failureWindow) {
    const start = input.failureWindow.startMs - FAILURE_WINDOW_PAD_MS;
    const end = input.failureWindow.endMs + FAILURE_WINDOW_PAD_MS;
    mapped.forEach((m, idx) => {
      if (m.absTs >= start && m.absTs <= end) setIfHigher(idx, 'failure-window');
    });
  }

  // 2. Nearest same-page frame to every real action.
  for (const action of input.actions) {
    const list = perPageSorted.get(action.pageId);
    if (!list) continue;
    setIfHigher(nearestIndex(list, input.clock.absFromAction(action)), 'action');
  }

  // 3. Nearest frame (any page) to every step boundary.
  for (const ts of input.stepBoundaryMs) {
    setIfHigher(nearestIndex(globalSorted, ts), 'step-boundary');
  }

  // 4. Cadence fill across idle stretches, bounded by the frame cap.
  const cadenceBudget = Math.max(0, maxFrames - kept.size);
  if (cadenceBudget > 0) {
    let added = 0;
    let lastTs: number | null = null;
    for (let idx = 0; idx < mapped.length; idx++) {
      if (added >= cadenceBudget) break;
      const ts = mapped[idx]!.absTs;
      if (kept.has(idx)) {
        lastTs = ts;
        continue;
      }
      if (lastTs === null || ts - lastTs >= cadenceMs) {
        kept.set(idx, 'cadence');
        lastTs = ts;
        added++;
      }
    }
  }

  // Ordered kept frames (by time) → sequential frame index + honest gap marking.
  const keptIndexes = [...kept.keys()].sort((a, b) => mapped[a]!.absTs - mapped[b]!.absTs);
  const result: KeptScreencastFrame[] = [];
  let prevRawIdx = -1;
  keptIndexes.forEach((rawIdx, frameIndex) => {
    const m = mapped[rawIdx]!;
    result.push({
      frameIndex,
      timestampMs: m.absTs,
      wallTimeMs: m.wallTimeMs,
      pageId: m.pageId,
      file: `${WORKSPACE_PATHS.browserScreencast}/${screencastFrameBasename(frameIndex, m.absTs)}`,
      keptReason: kept.get(rawIdx)!,
      // Raw frames existed between the previous kept frame and this one.
      decimatedBefore: rawIdx - prevRawIdx > 1,
    });
    prevRawIdx = rawIdx;
  });

  // Write JPEGs + index. Per-file failures don't block siblings.
  const writeOutcomes = await Promise.all(
    result.map(async (frame, i) => {
      try {
        await fs.writeFile(
          resolveWorkspacePath(input.workspaceRoot, frame.file),
          mapped[keptIndexes[i]!]!.jpeg,
        );
        return true;
      } catch {
        return false;
      }
    }),
  );
  const writeFailures = writeOutcomes.filter((ok) => !ok).length;

  const meta = {
    meta: true,
    rawFrameCount: rawFrames.length,
    keptFrameCount: result.length,
    deduped: true,
    decimationCadenceMs: cadenceMs,
    maxFrames,
    approxAlignment: input.clock.approxAlignment,
  };
  const lines = [JSON.stringify(meta), ...result.map((f) => JSON.stringify(f))];
  try {
    await fs.writeFile(indexPath, `${lines.join('\n')}\n`, 'utf-8');
  } catch {
    return { partial: true, frames: result };
  }

  return { partial: writeFailures > 0, frames: result };
};

const PRIORITY: Record<KeptReason, number> = {
  'failure-window': 3,
  action: 2,
  'step-boundary': 1,
  cadence: 0,
};

const writeIndexMarker = async (indexPath: string, reason: string): Promise<void> => {
  await fs.writeFile(indexPath, `${JSON.stringify({ meta: true, partial: true, reason })}\n`, 'utf-8');
};
